<template lang="pug">
.author-history.flex.flex-col.h-screen.w-screen.dark_bg-gray-700
  .author-history-bar.flex.gap-2.p-2.z-50.bg-gray-50.h-14.items-center.dark_bg-gray-900
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/editor/${firmCode}_${courseId}` : `#/editor/${courseId}`"
        )
          | Edit #[b {{ baseYamlFilename }}]

    button.ms-auto.cursor-pointer.bg-primary-600.hover_bg-primary-700.text-white.text-center.font-bold.py-2.px-4.rounded(
      @click="toggleButtonText"
      type="button"
    ) {{ buttonText }}

  .flex-1.flex.gap-2.flex-col.overflow-y-auto.p-10.items-center
    .max-w-screen-md.w-full
      .prose
        div
          h1.text-3xl.font-bold.dark_text-white.text-gray-800 {{ yamlName }} history

      .w-full.text-center(v-if="versions.length === 0")
        .text-center.italic.text-gray-800.text-sm.animate-loading Loading history
      .flex.gap-2.flex-col(v-else="")
        p.text-gray-800.dark_text-white.italic.mb-6(v-if="versions.length > 50")
          span <b>{{ versions.length }}</b> versions.
          span &nbsp;Latest <b>50</b> saves have diffs loaded.

        template(:key="item.key" v-for="(item, index) in processedVersions")

          // Published entries
          .prose.dark_prose-invert.max-w-none.text-sm.p-2.bg-gray-100.dark_bg-gray-800.rounded-lg(v-if="item.type === 'published'" :title="JSON.stringify(item.version)") 
            details.list-type.group
              summary.w-full.flex 
                span.pr-2.opacity-50
                  span.hidden.group-open_inline ▼
                  span.inline.group-open_hidden ▶
                span
                  strong published
                  | #{' '}
                  span [{{ item.version.publishTo === 'test' ? '🧪' : '🚚' }}]  
                  | #{' '}
                  span [{{ item.version.level }}]
                  | #{' '}
                  span &ndash; {{ item.version.user }} 

                FormatAgo.ms-auto.me-10(:date="item.version.date || 0")

              div
                p Client-facing message: {{ item.version.message }} 
                p Internal notes: {{ item.version.internal }} 

          // Grouped publish-sync entries
          Disclosure(v-slot="{ open }" v-else-if="item.type === 'publishSyncGroup'")
            .rounded-lg.w-full.bg-blue-50.dark_bg-blue-900.overflow-hidden.ring-2.transition(:class="[open ? 'ring-blue-500 my-2' : 'ring-transparent']")
              DisclosureButton.flex.w-full.rounded-lg.bg-blue-50.dark_bg-blue-900.px-4.py-2.text-left.text-sm.font-medium.text-gray-900.dark_text-gray-100.hover_bg-blue-100.dark_hover_bg-blue-800.focus_outline-none.focus-visible_ring.focus-visible_ring-blue-500.focus-visible_ring-opacity-75
                .flex.flex-1.gap-4.items-center
                  p.flex.flex-1.gap-2.items-center.me-auto
                    span.font-bold 📤 [PUBLISHED] Update metadata: synced {{ item.versions.length }} sections
                    span &ndash;
                    span {{ item.versions[0]?.author || 'Unknown' }}

                  .ms-auto

                  FormatAgo.px-2(:date="+item.versions[0]?.metadata?.createdDate")

                  Icon.transition(
                    :class="{ 'transform rotate-180': open }"
                    icon="ph:caret-down-bold"
                  )
              transition-expand
                DisclosurePanel.bg-blue-100.dark_bg-blue-800.rounded-lg.p-2
                  .space-y-2
                    div.px-2.py-1.text-sm.font-semibold.text-gray-700.dark_text-gray-200
                      | Synced sections ({{ item.versions.length }}):
                    div(v-for="(v, idx) in item.versions" :key="idx" class="px-4 py-1 text-sm bg-white dark_bg-gray-700 rounded")
                      span.text-gray-700.dark_text-gray-200 {{ v.commitMessage?.replace('📤 [PUBLISHED] ', '') || 'Unknown section' }}
                      span.text-gray-500.dark_text-gray-400.ml-2.text-xs &ndash; {{ v.author }}

          // Regular saved entries
          Disclosure(v-slot="{ open }" v-else="")
            .rounded-lg.w-full.bg-gray-200.overflow-hidden.ring-2.transition(:class="[open ? 'ring-gray-500 my-2' : 'ring-transparent']")
              DisclosureButton.flex.w-full.rounded-lg.bg-gray-200.px-4.py-2.text-left.text-sm.font-medium.text-gray-900.hover_bg-primary-100.focus_outline-none.focus-visible_ring.focus-visible_ring-primary-500.focus-visible_ring-opacity-75
                .flex.flex-1.gap-4.items-center
                  p.flex.flex-1.gap-2.items-center.me-auto
                    span.font-bold {{ item.version.commitMessage }}
                    span &ndash;
                    span {{ item.version.author }}

                  .ms-auto

                  FormatAgo.px-2(:date="+item.version.metadata?.createdDate")

                  Icon.transition(
                    :class="{ 'transform rotate-180': open }"
                    icon="ph:caret-down-bold"
                  )
              transition-expand
                DisclosurePanel.bg-gray-50.rounded-lg
                  div(v-if="showingDiff")
                    .border-b.border-gray-500
                    PrettyDiff(:diff="createDiffStringForVersion(item.originalIndex, 'yaml')" :name="yamlName")
                    .border-b.border-gray-500
                    PrettyDiff(:diff="createDiffStringForVersion(item.originalIndex, 'css')" :name="cssName")
                  div(v-else="")
                    .border-b.border-gray-500
                    div.px-2.py-1.text-sm.bg-gray-200.text-black
                      span {{ yamlName }}
                      span.italic(v-if="!item.version.yaml") &nbsp;– None found/loaded for this version of the course.
                    textarea.w-full.bg-transparent.font-monospace.p-4.text-black(
                      v-if="item.version.yaml"
                      :value="item.version.yaml"
                      readonly=""
                      rows="10"
                    )
                    .border-b.border-gray-500
                    div.px-2.py-1.text-sm.bg-gray-200.text-black 
                      span {{ cssName }}
                      span.italic(v-if="!item.version.css") &nbsp;– None found/loaded for this version of the course.
                    textarea.w-full.bg-transparent.font-monospace.p-4.text-black(
                      v-if="item.version.css"
                      :data-value="item.version.css" 
                      :value="item.version.css"
                      readonly=""
                      rows="10"
                    )
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { getAllVersions, getPublished } from '../lib/callModContentAuthors';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import MainMenu from './MainMenu.vue';
import FormatAgo from './FormatAgo.vue';
import { Icon } from '@iconify/vue';
import TransitionExpand from './TransitionExpand.vue';
import { createPatch } from 'diff';
import PrettyDiff from './PrettyDiff.vue';

const props = defineProps<{ courseId: number; firmCode?: string }>();

const baseYamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

const showingDiff = ref(true);

const buttonText = computed(() =>
  showingDiff.value ? 'Show full' : 'Show diff'
);

const toggleButtonText = () => {
  showingDiff.value = !showingDiff.value;
};

const versions = ref<(CourseResponse | PublishBody)[]>([]);

const normalizeWhitespace = s => s.replace(/\r\n/g, '\n').replace(/\s+$/gm, '');

const findNextVersionWithType = (index, type) => {
  for (let i = index + 1; i < versions.value.length; i++) {
    if (versions.value[i]?.[type]) {
      return versions.value[i];
    }
  }
  return null;
};

const createDiffString = (index, type) => {
  const currentVersion = versions.value?.[index]?.[type] || '';
  const olderVersion = findNextVersionWithType(index, type)?.[type] || '';

  const diffString = createPatch(
    `version.${type}`,
    normalizeWhitespace(olderVersion),
    normalizeWhitespace(currentVersion)
  );

  return diffString;
};

// Helper to create diff string for a processed version by its original index
const createDiffStringForVersion = (originalIndex, type) => {
  const currentVersion = versions.value?.[originalIndex]?.[type] || '';
  const olderVersion =
    findNextVersionWithType(originalIndex, type)?.[type] || '';

  const diffString = createPatch(
    `version.${type}`,
    normalizeWhitespace(olderVersion),
    normalizeWhitespace(currentVersion)
  );

  return diffString;
};

// Check if a version is a publish-sync entry
const isPublishSync = version => {
  if ('date' in version) return false; // Published entries are not publish-sync
  const commitMessage = version.commitMessage || '';
  return commitMessage.startsWith('📤 [PUBLISHED]');
};

// Process versions to group consecutive publish-sync entries
const processedVersions = computed(() => {
  const processed: any[] = [];
  const PUBLISH_SYNC_WINDOW = 5000; // 5 seconds in milliseconds

  for (let i = 0; i < versions.value.length; i++) {
    const version = versions.value[i];

    // If it's a published entry, add as-is
    if ('date' in version) {
      processed.push({
        type: 'published',
        version: version,
        key: `published-${i}`,
        originalIndex: i
      });
      continue;
    }

    // If it's not a publish-sync entry, add as regular entry
    if (!isPublishSync(version)) {
      processed.push({
        type: 'regular',
        version: version,
        key: `regular-${i}`,
        originalIndex: i
      });
      continue;
    }

    // It's a publish-sync entry - check if we should group it with consecutive ones
    const currentTime = +(version.metadata?.createdDate || 0);
    const publishGroup: any[] = [version];
    let groupIndices = [i];
    let j = i + 1;

    // Collect consecutive publish-sync entries within the time window
    while (j < versions.value.length) {
      const nextVersion = versions.value[j];

      // Stop if it's a published entry or not a publish-sync entry
      if ('date' in nextVersion || !isPublishSync(nextVersion)) {
        break;
      }

      const nextTime = +(nextVersion.metadata?.createdDate || 0);
      const timeDiff = Math.abs(currentTime - nextTime);

      // Stop if time difference is too large (not consecutive)
      if (timeDiff > PUBLISH_SYNC_WINDOW) {
        break;
      }

      publishGroup.push(nextVersion);
      groupIndices.push(j);
      j++;
    }

    // Add grouped entry
    processed.push({
      type: 'publishSyncGroup',
      versions: publishGroup,
      key: `publishSyncGroup-${i}`,
      originalIndex: i,
      groupIndices: groupIndices
    });

    // Skip the grouped indices in the main loop
    i = j - 1;
  }

  return processed;
});

const yamlName = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

const cssName = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.css`
    : `${props.courseId}.css`
);

onMounted(async () => {
  const { courseId, firmCode } = props;
  const compactSaved = await getAllVersions({ courseId, firmCode }, true);
  const recentFullSaved = await getAllVersions(
    { courseId, firmCode },
    false,
    50
  );

  // merge the latest full (yaml/css) results with the compact results
  const mergedSaved = compactSaved.map(compact => {
    const full = recentFullSaved.find(f => f.id === compact.id);
    return full ? { ...compact, ...full } : compact;
  });

  const published =
    (await getPublished({ courseId, firmCode }).catch(() => [])) || [];

  // sort the saved and published versions together by most recent first
  const allVersions = [...mergedSaved, ...published]
    .filter(item => {
      return props.firmCode ? item.firmCode === props.firmCode : true;
    })
    .sort((a, b) => {
      const dateA = 'date' in a ? a.date || 0 : +(a.metadata?.createdDate || 0);
      const dateB = 'date' in b ? b.date || 0 : +(b.metadata?.createdDate || 0);

      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

  versions.value = allVersions;
});
</script>
